import React from 'react';
import { Column } from 'react-table';
import { formatCurrency } from 'utils';
import { HubProposal } from '../../../api/types';
import { getPersonaName } from '../../../lib/getPersonaName';
import { CellArgs, ProposalTableActions } from '../../../types';
import { proposalSaleTypeToViewText } from '../../../constants';
import { SimpleTextCell, StageCell, StatusCell, DateCell, CustomerCell, ActionCell } from '../cells';

interface Options extends ProposalTableActions<HubProposal> {
  tableData: HubProposal[];
  sorting?: {
    sortKey: string;
    sortOrder: 'ASC' | 'DESC';
    onSortChange: (key: string, order: 'ASC' | 'DESC') => void;
  };
  filtering?: {
    filters: Record<string, any>;
    onFilterChange: (filterKey: string, filterValue: any) => void;
  };
}

type HubProposalCellArgs = CellArgs<HubProposal>;

export function createTableColumns({ tableData, sorting, filtering, ...actions }: Options) {
  const columns: Column<HubProposal>[] = [
    {
      width: 150,
      sortable: true,
      resizable: false,
      Header: 'Created',
      accessor: 'createdDateTime',
      headerClassName: 'customHeader',
      Cell: ({ original }: HubProposalCellArgs) => <DateCell proposal={original} />,
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Customer',
      accessor: 'businessName',
      headerClassName: 'customHeader',
      Cell: ({ original }: HubProposalCellArgs) => <CustomerCell proposal={original} />,
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Contact',
      accessor: 'firstName',
      headerClassName: 'customHeader',
      Cell: ({ original }: HubProposalCellArgs) => <SimpleTextCell value={getPersonaName(original)} />,
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Sale Type',
      accessor: 'saleType',
      headerClassName: 'customHeader',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell value={original.saleType ? proposalSaleTypeToViewText[original.saleType] : null} />
      ),
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Sales Rep',
      headerClassName: 'customHeader',
      accessor: 'vendorOpportunity.salesRepresentative.fullName',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell value={getPersonaName(original?.vendorOpportunity?.salesRepresentative)} />
      ),
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Finance Manager',
      headerClassName: 'customHeader',
      headerStyle: { minWidth: '10rem' },
      style: { minWidth: '10rem' },
      accessor: 'vendorOpportunity.financeManager.fullName',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell value={getPersonaName(original?.vendorOpportunity?.financeManager)} />
      ),
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Location',
      headerClassName: 'customHeader',
      accessor: 'vendorOpportunity.location.locationName',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell value={original?.vendorOpportunity?.location?.locationName} />
      ),
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Equipment Condition',
      headerClassName: 'customHeader',
      accessor: 'vendorOpportunity.equipments[0].newUsed',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell
          value={
            original?.vendorOpportunity?.equipments[0]?.newUsed
              ? original?.vendorOpportunity?.equipments[0]?.newUsed.toLowerCase()
              : 'Unknown'
          }
        />
      ),
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Payment Method',
      headerClassName: 'customHeader',
      accessor: 'vendorOpportunity.paymentType',
      Cell: ({ original }: HubProposalCellArgs) => <SimpleTextCell value={original?.vendorOpportunity?.paymentType} />,
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Amount',
      accessor: 'amountRequested',
      headerClassName: 'customHeader',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell formatter={formatCurrency} value={original.amountRequested} />
      ),
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Opportunity Stage',
      headerClassName: 'customHeader',
      headerStyle: { minWidth: '11rem' },
      style: { minWidth: '11rem' },
      accessor: 'vendorOpportunity.applicationStage',
      Cell: ({ original }: HubProposalCellArgs) => <StageCell proposal={original} />,
    },
    {
      sortable: true,
      resizable: false,
      accessor: 'status',
      Header: 'Proposal Status',
      headerClassName: 'customHeader',
      headerStyle: { minWidth: '11rem' },
      style: { minWidth: '11rem' },
      Cell: ({ original }: HubProposalCellArgs) => <StatusCell proposal={original} />,
    },
    {
      sortable: true,
      resizable: false,
      Header: 'Created By',
      accessor: 'salesPersonName',
      headerClassName: 'customHeader',
      Cell: ({ original }: HubProposalCellArgs) => <SimpleTextCell value={original.salesPersonName} />,
    },
    {
      sortable: false,
      resizable: false,
      filterable: false,
      Header: 'Actions',
      style: { textAlign: 'center' },
      headerClassName: 'customHeader',
      headerStyle: { textAlign: 'center' },
      Cell: ({ original }: HubProposalCellArgs) => <ActionCell proposal={original} {...actions} />,
    },
  ];

  return columns;
}
